import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getCountries,
  getDepartments,
  getProvinces,
  getDistricts,
} from "../../../../lib/locationApis";
import { updateLocation } from "../../../../redux/locationReducer";
import {
  validateShippingData,
  ValidationError,
} from "../../../../utils/orderUtils";
import { scrollToError } from "../../../../utils/scrollUtils";
import ErrorSummary from "../../../../components/ErrorSummary/ErrorSummary";
import styles from "./Shipping.module.css";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ShippingProps {
  onConfirm: () => void;
  onChange: (data: any) => void;
  onEdit: () => void;
}

const Shipping: React.FC<ShippingProps> = ({ onConfirm, onChange, onEdit }) => {
  const dispatch = useDispatch();
  const location = useSelector((state: any) => state.location);

  // Form refs for scrolling to errors
  const formRefs = useRef<
    Record<
      string,
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
    >
  >({
    email: null,
    country: null,
    department: null,
    province: null,
    district: null,
    address: null,
    reference: null,
    orderComments: null,
  });

  const sectionRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    email: "",
    country: location.country || "",
    department: location.department || "",
    province: location.province || "",
    district: location.district || "",
    address: location.address || "",
    ubigeoCode: location.ubigeoCode || "",
    reference: "",
    orderComments: "",
  });

  const [formErrors, setFormErrors] = useState<Record<string, ValidationError>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);

  useEffect(() => {
    getCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (formData.country) {
      getDepartments()
        .then(setDepartments)
        .catch(() => setDepartments([]));
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.department) {
      getProvinces(formData.department)
        .then(setProvinces)
        .catch(() => setProvinces([]));
    }
  }, [formData.department]);

  useEffect(() => {
    if (formData.province) {
      getDistricts(formData.department, formData.province)
        .then(setDistricts)
        .catch(() => setDistricts([]));
    }
  }, [formData.province, formData.department]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when field is edited
    if (formErrors[name]) {
      const newErrors = { ...formErrors };
      delete newErrors[name];
      setFormErrors(newErrors);
    }
  };

  const handleLocationChange = (field: string, value: string) => {
    const updatedFormData = { ...formData, [field]: value };

    if (field === "country") {
      updatedFormData.department = "";
      updatedFormData.province = "";
      updatedFormData.district = "";
    } else if (field === "department") {
      updatedFormData.province = "";
      updatedFormData.district = "";
    } else if (field === "province") {
      updatedFormData.district = "";
    }

    setFormData(updatedFormData);

    // Clear error when field is edited
    if (formErrors[field]) {
      const newErrors = { ...formErrors };
      delete newErrors[field];
      setFormErrors(newErrors);
    }
  };

  const scrollToField = (fieldId: string) => {
    const element = formRefs.current[fieldId];
    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    const validationResult = validateShippingData(formData);

    if (validationResult.hasErrors) {
      setFormErrors(validationResult.errors);
      setShowErrors(true);
      setIsSubmitting(false);

      // Scroll to error summary
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      // Set focus on first field with error
      if (validationResult.firstErrorField) {
        setTimeout(() => {
          scrollToField(validationResult.firstErrorField!);
        }, 300);
      }

      return;
    }

    setFormErrors({});
    setShowErrors(false);
    setIsConfirmed(true);
    onChange(formData);
    dispatch(
      updateLocation({
        country: formData.country,
        department: formData.department,
        province: formData.province,
        district: formData.district,
        address: formData.address,
        ubigeoCode: formData.ubigeoCode,
      })
    );
    onConfirm();
    setIsSubmitting(false);
  };

  const handleEdit = () => {
    setIsConfirmed(false);
    onEdit();
  };

  return (
    <div className={styles.card} ref={sectionRef}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Paso 1 / 3: Info de Envío</h2>
        {isConfirmed && <CheckCircleOutlineIcon />}
      </div>
      <div className={styles.cardContent}>
        {!isConfirmed ? (
          <>
            {/* Error Summary */}
            {showErrors && Object.keys(formErrors).length > 0 && (
              <ErrorSummary errors={formErrors} onErrorClick={scrollToField} />
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="email">
                  Correo Electrónico <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`${styles.input} ${
                      formErrors.email ? styles.errorInput : ""
                    }`}
                    placeholder="Tu correo electrónico"
                    aria-invalid={formErrors.email ? "true" : "false"}
                    aria-describedby={
                      formErrors.email ? "email-error" : undefined
                    }
                    ref={(el) => (formRefs.current.email = el)}
                  />
                  {formErrors.email && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                <p className={styles.hint}>
                  A este correo te llegará el resumen de tu pedido y todo el
                  detalle de tu envío.
                </p>
                {formErrors.email && (
                  <span
                    id="email-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.email.message}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.divider}></div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="country">
                  País <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={(e) =>
                      handleLocationChange("country", e.target.value)
                    }
                    className={`${styles.select} ${
                      formErrors.country ? styles.errorInput : ""
                    }`}
                    onKeyDown={(e) => e.preventDefault()}
                    aria-invalid={formErrors.country ? "true" : "false"}
                    aria-describedby={
                      formErrors.country ? "country-error" : undefined
                    }
                    ref={(el) => (formRefs.current.country = el)}
                  >
                    <option value="">Seleccione un país</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.country && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.country && (
                  <span
                    id="country-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.country.message}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="department">
                  Departamento <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={(e) =>
                      handleLocationChange("department", e.target.value)
                    }
                    className={`${styles.select} ${
                      formErrors.department ? styles.errorInput : ""
                    }`}
                    disabled={!formData.country}
                    onKeyDown={(e) => e.preventDefault()}
                    aria-invalid={formErrors.department ? "true" : "false"}
                    aria-describedby={
                      formErrors.department ? "department-error" : undefined
                    }
                    ref={(el) => (formRefs.current.department = el)}
                  >
                    <option value="">Seleccione un departamento</option>
                    {departments.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                  {formErrors.department && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.department && (
                  <span
                    id="department-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.department.message}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="province">
                  Provincia <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <select
                    id="province"
                    name="province"
                    value={formData.province}
                    onChange={(e) =>
                      handleLocationChange("province", e.target.value)
                    }
                    className={`${styles.select} ${
                      formErrors.province ? styles.errorInput : ""
                    }`}
                    disabled={!formData.department}
                    onKeyDown={(e) => e.preventDefault()}
                    aria-invalid={formErrors.province ? "true" : "false"}
                    aria-describedby={
                      formErrors.province ? "province-error" : undefined
                    }
                    ref={(el) => (formRefs.current.province = el)}
                  >
                    <option value="">Seleccione una provincia</option>
                    {provinces.map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                  {formErrors.province && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.province && (
                  <span
                    id="province-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.province.message}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="district">
                  Distrito <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <select
                    id="district"
                    name="district"
                    value={formData.district}
                    onChange={(e) =>
                      handleLocationChange("district", e.target.value)
                    }
                    className={`${styles.select} ${
                      formErrors.district ? styles.errorInput : ""
                    }`}
                    disabled={!formData.province}
                    onKeyDown={(e) => e.preventDefault()}
                    aria-invalid={formErrors.district ? "true" : "false"}
                    aria-describedby={
                      formErrors.district ? "district-error" : undefined
                    }
                    ref={(el) => (formRefs.current.district = el)}
                  >
                    <option value="">Seleccione un distrito</option>
                    {districts.map((district) => (
                      <option key={district.id} value={district.distrito}>
                        {district.distrito}
                      </option>
                    ))}
                  </select>
                  {formErrors.district && (
                    <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  )}
                </div>
                {formErrors.district && (
                  <span
                    id="district-error"
                    className={styles.errorMessage}
                    role="alert"
                  >
                    {formErrors.district.message}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="address">
                Dirección de Entrega <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <input
                  id="address"
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`${styles.input} ${
                    formErrors.address ? styles.errorInput : ""
                  }`}
                  aria-invalid={formErrors.address ? "true" : "false"}
                  aria-describedby={
                    formErrors.address ? "address-error" : undefined
                  }
                  ref={(el) => (formRefs.current.address = el)}
                />
                {formErrors.address && (
                  <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                )}
              </div>
              {formErrors.address && (
                <span
                  id="address-error"
                  className={styles.errorMessage}
                  role="alert"
                >
                  {formErrors.address.message}
                </span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="reference">Referencia</label>
              <input
                id="reference"
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                className={styles.input}
                ref={(el) => (formRefs.current.reference = el)}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="orderComments">
                Comentarios sobre la entrega
              </label>
              <textarea
                id="orderComments"
                name="orderComments"
                value={formData.orderComments}
                onChange={handleChange}
                className={styles.textarea}
                ref={(el) => (formRefs.current.orderComments = el)}
              ></textarea>
            </div>

            {Object.keys(formErrors).length > 0 && showErrors && (
              <div className={styles.fixErrorsContainer}>
                <button
                  type="button"
                  className={styles.fixErrorsButton}
                  onClick={() => scrollToField(Object.keys(formErrors)[0])}
                >
                  <ErrorOutlineIcon className={styles.fixErrorsIcon} />
                  Corregir errores
                </button>
              </div>
            )}

            <button
              className={styles.confirmButton}
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Procesando..." : "Confirmar"}
            </button>
          </>
        ) : (
          <div className={styles.cardSummary}>
            <p>
              Correo: <strong>{formData.email}</strong>
            </p>
            <p>
              Dirección: <strong>{formData.address}</strong>,{" "}
              <strong>{formData.district}</strong>,{" "}
              <strong>{formData.province}</strong>,{" "}
              <strong>{formData.department}</strong>,{" "}
              <strong>{formData.country}</strong>
            </p>
            {formData.reference && (
              <p>
                Referencia: <strong>{formData.reference}</strong>
              </p>
            )}
            {formData.orderComments && (
              <p>
                Comentarios sobre la entrega:{" "}
                <strong>{formData.orderComments}</strong>
              </p>
            )}
            <button className={styles.editButton} onClick={handleEdit}>
              Editar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shipping;
